import { Link } from "react-router-dom";
import {
  BENEFIT_LABEL_VI,
  EMPLOYMENT_TYPE_LABEL_VI,
  GENDER_LABEL_VI,
  POSTING_STATE_LABEL_VI,
  SALARY_TYPE_LABEL_VI,
  SKILL_LABEL_VI,
  VENUE_TYPE_LABEL_VI,
  WORKER_POSITION_LABEL_VI,
  formatSalaryRange,
  type EmploymentType,
  type Gender,
  type PostingState,
  type SalaryType,
  type VenueType,
  type WorkerPosition,
} from "@chotca/config";
import { usePosting } from "@chotca/api-client";
import { ApplicantsTab } from "./applicants-tab";
import { IdentityRequiredGate } from "@/components/employer/identity-required-gate";
import { BoostPostingButton, BoostSummaryCard } from "./boost-posting";
import { PostingActions } from "./posting-actions";

export function PostingDetailClient({ id }: { id: string }) {
  const { data: posting, isPending, isError } = usePosting(id);

  if (isError) return null;

  if (isPending) {
    return (
      <section className="space-y-6">
        <div className="h-4 w-24 animate-pulse rounded bg-ink/10" />
        <div className="h-10 w-2/3 animate-pulse rounded bg-ink/10" />
        <div className="h-4 w-1/3 animate-pulse rounded bg-ink/10" />
        <div className="grid gap-4 md:grid-cols-2">
          <div className="h-40 animate-pulse rounded-xl bg-ink/5" />
          <div className="h-40 animate-pulse rounded-xl bg-ink/5" />
        </div>
      </section>
    );
  }

  if (!posting) return null;

  const salary = formatSalaryRange(
    posting.salaryMin as unknown as number | bigint | string,
    posting.salaryMax as unknown as number | bigint | string,
    SALARY_TYPE_LABEL_VI[posting.salaryType as SalaryType],
  );
  const positionLabel =
    WORKER_POSITION_LABEL_VI[posting.position as WorkerPosition] ??
    posting.position;
  const empLabel =
    EMPLOYMENT_TYPE_LABEL_VI[posting.employmentType as EmploymentType] ??
    posting.employmentType;
  const venueLabel =
    VENUE_TYPE_LABEL_VI[posting.venueType as VenueType] ?? posting.venueType;
  const reqs = posting.requirements;

  return (
    <section className="space-y-6">
      <nav className="mb-4 text-sm">
        <Link
          to="/postings"
          className="text-ink/60 hover:text-ink hover:underline"
        >
          ← Tin tuyển dụng
        </Link>
      </nav>

      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink/60">
            <span>{positionLabel}</span>
            <span>·</span>
            <span>{empLabel}</span>
            <span>·</span>
            <span>{venueLabel}</span>
          </div>
          <h1 className="mt-1 text-display-lg font-extrabold text-ink">
            {posting.title}
          </h1>
          <p className="mt-1 text-sm text-ink/60">
            {posting.org.name} · {posting.org.district}, {posting.org.city}
          </p>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-ink/10 px-2 py-0.5 font-bold uppercase">
              {POSTING_STATE_LABEL_VI[posting.state as PostingState] ??
                posting.state}
            </span>
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-bold text-emerald-800">
              {salary}
            </span>
            <span className="rounded-full bg-ink/5 px-2 py-0.5">
              Tuyển {posting.headcount} người
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {posting.state === "OPEN" ? (
            <BoostPostingButton
              postingId={posting.id}
              orgId={posting.orgId}
              isBoosted={posting.isBoosted ?? false}
              boostedUntil={posting.boostedUntil ?? null}
            />
          ) : null}
          <PostingActions postingId={posting.id} state={posting.state} />
        </div>
      </header>

      <div className="grid gap-4 md:grid-cols-2">
        <BoostSummaryCard postingId={posting.id} />

        <div className="rounded-xl border border-ink/10 bg-paper p-5">
          <h3 className="mb-2 text-sm font-semibold">Mô tả công việc</h3>
          <p className="whitespace-pre-wrap text-sm text-ink/80">
            {posting.description}
          </p>
          {posting.softNotes ? (
            <p className="mt-3 whitespace-pre-wrap text-sm italic text-ink/70">
              {posting.softNotes}
            </p>
          ) : null}
        </div>

        {posting.benefits.length > 0 ? (
          <div className="rounded-xl border border-ink/10 bg-paper p-5">
            <h3 className="mb-2 text-sm font-semibold">Quyền lợi</h3>
            <div className="flex flex-wrap gap-1.5">
              {posting.benefits.map((b) => (
                <span
                  key={b}
                  className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800"
                >
                  • {BENEFIT_LABEL_VI[b as keyof typeof BENEFIT_LABEL_VI] ?? b}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {posting.skillsRequired.length + posting.skillsPreferred.length > 0 ? (
          <div className="rounded-xl border border-ink/10 bg-paper p-5">
            <h3 className="mb-2 text-sm font-semibold">Kỹ năng</h3>
            {posting.skillsRequired.length > 0 ? (
              <div className="mb-2">
                <p className="mb-1 text-xs text-ink/60">Bắt buộc</p>
                <div className="flex flex-wrap gap-1.5">
                  {posting.skillsRequired.map((s) => (
                    <span
                      key={s}
                      className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-800"
                    >
                      {SKILL_LABEL_VI[s] ?? s}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            {posting.skillsPreferred.length > 0 ? (
              <div>
                <p className="mb-1 text-xs text-ink/60">Ưu tiên</p>
                <div className="flex flex-wrap gap-1.5">
                  {posting.skillsPreferred.map((s) => (
                    <span
                      key={s}
                      className="rounded-full bg-ink/5 px-2 py-0.5 text-xs"
                    >
                      {SKILL_LABEL_VI[s] ?? s}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {hasRequirement(reqs) ? (
          <div className="rounded-xl border border-ink/10 bg-paper p-5">
            <h3 className="mb-2 text-sm font-semibold">Yêu cầu ứng viên</h3>
            <ul className="space-y-1 text-sm text-ink/80">
              {reqs.gender ? (
                <li>
                  • Giới tính:{" "}
                  {GENDER_LABEL_VI[reqs.gender as Gender] ?? reqs.gender}
                </li>
              ) : null}
              {reqs.minAge || reqs.maxAge ? (
                <li>
                  • Tuổi: {reqs.minAge ?? "—"} – {reqs.maxAge ?? "—"}
                </li>
              ) : null}
              {reqs.minHeightCm ? (
                <li>• Cao tối thiểu: {reqs.minHeightCm}cm</li>
              ) : null}
              {reqs.motorbikeRequired ? <li>• Cần có xe máy</li> : null}
              {reqs.excludeVisibleTattoo ? (
                <li>• Không có hình xăm visible</li>
              ) : null}
              {reqs.kycCccdRequired ? <li>• Cần xác thực CCCD</li> : null}
            </ul>
          </div>
        ) : null}
      </div>

      <h2 className="mt-8 mb-3 text-lg font-semibold">Ứng viên</h2>
      <IdentityRequiredGate>
        <ApplicantsTab
          postingId={posting.id}
          orgId={posting.orgId}
          postingState={posting.state as PostingState}
        />
      </IdentityRequiredGate>
    </section>
  );
}

function hasRequirement(r: {
  gender?: string | null;
  minAge?: number | null;
  maxAge?: number | null;
  minHeightCm?: number | null;
  motorbikeRequired?: boolean;
  excludeVisibleTattoo?: boolean;
  kycCccdRequired?: boolean;
}): boolean {
  return Boolean(
    r.gender ||
    r.minAge ||
    r.maxAge ||
    r.minHeightCm ||
    r.motorbikeRequired ||
    r.excludeVisibleTattoo ||
    r.kycCccdRequired,
  );
}
